import React, { useMemo } from 'react';
import { Box, FormGroup, Typography } from '@mui/material';
import { useCalendarDefaults } from 'shared/hooks/useCalendarDefaults';
import { isShiftClaimState, stateLabels } from 'shared/utils/shift-claim-state';
import {
  LwButton,
  LwCheckbox,
  LwMultiSelectUncontrolled,
  MultiDateCalendar,
  StyledActionsBox,
  StyledCheckboxGroup,
  StyledCircularProgress,
  StyledContentBox,
  StyledDivider,
  StyledErrorMessage,
  StyledForm,
  StyledFormControl,
  StyledTitle,
} from 'redesign';
import { useFlexWorkerAutocomplete } from 'ogp/components/views/administration/shared/components/filter-form/use-flexworker-autocomplete';
import { useCalendar } from 'shared/hooks/use-calendar';
import { useGetCheckoutsArchiveFilters } from '../../hooks/use-get-checkouts-archive-filters';
import type { ArchiveFilters as TArchiveFilters } from '../../../../shared/hooks/use-checkouts-archive-page.types';

const CheckoutsArchiveFilters = ({
  filters,
  onCancel,
  onSubmit,
}: {
  filters: TArchiveFilters;
  onCancel: () => void;
  onSubmit: (data: TArchiveFilters) => void;
}) => {
  const [resetKey, hasBeenReset, reset] = useReset();
  const { monthLabels, dayLabels } = useCalendarDefaults();
  const { status, data } = useGetCheckoutsArchiveFilters();
  const branchOffices = data?.branchOffices ?? [];
  const flexWorkers = data?.flexWorkers ?? [];
  const states = data?.states ?? [];
  const labeledStates = states.map((s) => ({ label: stateLabels[s], value: s }));
  const optionLabels = useMemo(() => {
    const labels: { [key: string]: string } = {};
    flexWorkers.forEach((i) => (labels[i.id] = i.fullName));
    return labels;
  }, [flexWorkers]);

  const { flexWorkerOptions, resetFlexWorkers, selectFlexWorkers, selectedFlexWorkers } =
    useFlexWorkerAutocomplete({
      available: flexWorkers,
      initial: filters.flexWorkerIds,
    });

  const memoizedSelectedFlexWorkers = useMemo(
    () => selectedFlexWorkers.map((id) => ({ id, name: optionLabels[id] })) ?? [],
    [selectedFlexWorkers, optionLabels]
  );

  const { dateRange, handleDateChange, getDateRangeFrom, handleResetDateRange } = useCalendar({
    fromDate: filters.fromDate,
    toDate: filters.toDate,
  });

  const handleReset = () => {
    handleResetDateRange();
    resetFlexWorkers();
    reset();
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const keys = [...formData.keys()];
    const result: TArchiveFilters = {
      departmentIds: keys
        .filter((key) => key.startsWith(formElementNames.department))
        .map((key) => key.replace(formElementNames.department, '')),
      flexWorkerIds: selectedFlexWorkers,
      states: keys
        .filter((key) => key.startsWith(formElementNames.state))
        .map((key) => key.replace(formElementNames.state, ''))
        .filter(isShiftClaimState),
      ...dateRange,
    };

    onSubmit(result);
  };

  const jsx: Record<typeof status, React.ReactElement> = {
    error: (
      <StyledErrorMessage variant="body2">
        De filters konden niet opgehaald worden.
      </StyledErrorMessage>
    ),
    loading: <StyledCircularProgress size={48} />,
    idle: <StyledCircularProgress size={48} />,
    success: (
      <>
        <StyledTitle variant="body1">
          Selecteer een of meer vestigingen om enkel checkouts van die vestiging(en) te zien.
        </StyledTitle>
        <FormGroup row>
          {branchOffices.map((branchOffice) => (
            <StyledCheckboxGroup key={`${branchOffice.id}-${resetKey}`}>
              <Typography variant="body1">{branchOffice.name}</Typography>
              {branchOffice.departments.map((department) => (
                <StyledFormControl
                  key={department.id}
                  label={department.name}
                  control={
                    <LwCheckbox
                      name={`${formElementNames.department}${department.id}`}
                      defaultChecked={
                        hasBeenReset ? false : (filters.departmentIds ?? []).includes(department.id)
                      }
                      title={department.name}
                    />
                  }
                />
              ))}
            </StyledCheckboxGroup>
          ))}
        </FormGroup>
        <StyledDivider />
        <StyledTitle variant="body1">
          Selecteer een of meer statussen om enkel checkouts met die status te zien.
        </StyledTitle>
        <FormGroup row>
          {labeledStates.map((state) => (
            <StyledFormControl
              label={state.label}
              key={`${state.value}-${resetKey}`}
              control={
                <LwCheckbox
                  name={`${formElementNames.state}${state.value}`}
                  defaultChecked={
                    hasBeenReset ? false : (filters.states ?? []).includes(state.value)
                  }
                  title={state.label}
                />
              }
            />
          ))}
        </FormGroup>
        <StyledDivider />
        <StyledTitle variant="body1">
          Selecteer een of meer flexwerkers om enkel checkouts van die flexwerker(s) te zien.
        </StyledTitle>

        <LwMultiSelectUncontrolled
          value={memoizedSelectedFlexWorkers}
          onChange={(_e, values) => {
            selectFlexWorkers(values.map((i) => i.id));
          }}
          options={flexWorkerOptions}
          getOptionKey={(option) => option.id}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </>
    ),
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <StyledContentBox>
        <StyledTitle variant="h2">Filter Checkouts</StyledTitle>
        <MultiDateCalendar
          sx={{ mb: 4, width: '100%' }}
          weekStartDayIndex={1}
          range
          months={monthLabels}
          weekDays={dayLabels}
          value={getDateRangeFrom(dateRange.fromDate, dateRange.toDate)}
          maxDate={TODAY}
          onChange={handleDateChange}
        />
        <Box flex="1" justifyContent="flex-end">
          <LwButton color="secondary" onClick={handleResetDateRange}>
            Datumselectie wissen
          </LwButton>
        </Box>
        <StyledDivider />
        {jsx[status]}
      </StyledContentBox>
      <StyledActionsBox>
        <Box flexBasis="100%">
          <LwButton color="primary" type="submit">
            Filter
          </LwButton>
        </Box>

        <LwButton color="secondary" onClick={onCancel}>
          Terug
        </LwButton>
        <LwButton color="secondary" onClick={handleReset}>
          Filters wissen
        </LwButton>
      </StyledActionsBox>
    </StyledForm>
  );
};

const formElementNames = {
  state: 'state-',
  department: 'department-',
};

const TODAY = new Date();

const useReset = () => {
  const [counter, setCounter] = React.useState(0);
  const hasBeenReset = counter > 0;

  const reset = () => {
    setCounter((cur) => cur + 1);
  };

  return [counter, hasBeenReset, reset] as const;
};

export { CheckoutsArchiveFilters };
